import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Doc } from '../../../../convex/_generated/dataModel';
import { toast } from 'sonner';

type ReferenceDoc = Doc<'references'> & {
  imageUrl?: string | null;
  sourceURL?: string | null;
  tags: Doc<'tags'>[];
  mediaItems: (Doc<'mediaItems'> & {
    url: string | null;
    mediaType: 'image' | 'video';
  })[];
  app?: Doc<'apps'> | null;
};

export type PromptType = 
  | 'react-component'
  | 'full-page'
  | 'feature-implementation'
  | 'design-system'
  | 'ui-pattern'
  | 'tailwind-theme'
  | 'custom';

interface PromptTypeOption {
  value: PromptType;
  label: string;
  description: string;
}

const PROMPT_TYPES: PromptTypeOption[] = [
  {
    value: 'react-component',
    label: 'React Component',
    description: 'Generate a prompt for creating a reusable React component'
  },
  {
    value: 'full-page',
    label: 'Full Page',
    description: 'Generate a prompt for creating a complete page layout'
  },
  {
    value: 'feature-implementation',
    label: 'Feature Implementation',
    description: 'Generate a prompt for implementing a specific feature or functionality'
  },
  {
    value: 'design-system',
    label: 'Design System Component',
    description: 'Generate a prompt for creating a design system component'
  },
  {
    value: 'ui-pattern',
    label: 'UI Pattern',
    description: 'Generate a prompt for implementing a UI pattern or interaction'
  },
  {
    value: 'tailwind-theme',
    label: 'Tailwind Theme',
    description: 'Generate a prompt for creating a Tailwind CSS theme based on image colors'
  },
  {
    value: 'custom',
    label: 'Custom Prompt',
    description: 'Generate a customizable prompt with your own specifications'
  }
];

interface ErrorInfo {
  type: 'network' | 'rate_limit' | 'api_key' | 'server' | 'unknown';
  message: string;
  canRetry: boolean;
}

interface AIPromptGeneratorModalProps {
  reference: ReferenceDoc;
  isOpen: boolean;
  onClose: () => void;
  onGeneratePrompt: (reference: ReferenceDoc, promptType: PromptType, customInstructions?: string) => Promise<string>;
}

export function AIPromptGeneratorModal({ 
  reference, 
  isOpen, 
  onClose, 
  onGeneratePrompt 
}: AIPromptGeneratorModalProps) {
  const [selectedPromptType, setSelectedPromptType] = useState<PromptType>('react-component');
  const [customInstructions, setCustomInstructions] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Save prompt state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string>('');

  // Accessibility refs
  const modalRef = useRef<HTMLDivElement>(null);
  const generateButtonRef = useRef<HTMLButtonElement>(null);
  const customInstructionsRef = useRef<HTMLTextAreaElement>(null);
  const saveTitleRef = useRef<HTMLInputElement>(null);

  // Convex mutation for saving prompts
  const savePrompt = useMutation(api.savedPrompts.savePrompt);

  // Focus management
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (showSaveDialog && saveTitleRef.current) {
      saveTitleRef.current.focus();
    }
  }, [showSaveDialog]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setGeneratedPrompt('');
      setErrorInfo(null);
      setCopySuccess(false);
      setCustomInstructions('');
      setShowSaveDialog(false);
      setSaveTitle('');
      setSaveDescription('');
      setSaveSuccess(false);
      setSaveError('');
      setRetryCount(0);
    }
  }, [isOpen]);

  // Clear copy success message after 3 seconds
  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => setCopySuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  // Clear save success message after 3 seconds
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  const parseError = (err: unknown): ErrorInfo => {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    
    // Rate limiting detection
    if (errorMessage.includes('rate limit') || errorMessage.includes('429') || errorMessage.includes('quota')) {
      return {
        type: 'rate_limit',
        message: 'API rate limit exceeded. Please wait a moment before trying again.',
        canRetry: true
      };
    }
    
    // API key issues
    if (errorMessage.includes('api key') || errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
      return {
        type: 'api_key',
        message: 'API authentication failed. Please check your API key configuration.',
        canRetry: false
      };
    }
    
    // Network issues
    if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('ECONNREFUSED')) {
      return {
        type: 'network',
        message: 'Network connection failed. Please check your internet connection.',
        canRetry: true
      };
    }
    
    // Server errors
    if (errorMessage.includes('500') || errorMessage.includes('502') || errorMessage.includes('503')) {
      return {
        type: 'server',
        message: 'Server error occurred. The service may be temporarily unavailable.',
        canRetry: true
      };
    }
    
    return {
      type: 'unknown',
      message: errorMessage,
      canRetry: true
    };
  };

  const handleGeneratePrompt = async () => {
    setIsGenerating(true);
    setErrorInfo(null);
    setGeneratedPrompt('');

    try {
      const prompt = await onGeneratePrompt(
        reference, 
        selectedPromptType, 
        selectedPromptType === 'custom' ? customInstructions : undefined
      );
      setGeneratedPrompt(prompt);
      setRetryCount(0);
      
      // Success toast
      toast.success('AI prompt generated successfully!', {
        description: 'Your prompt is ready to use.',
      });
      
      // Announce to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = 'AI prompt has been generated successfully. The prompt is now available below.';
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);
      
    } catch (err) {
      const errorInfo = parseError(err);
      setErrorInfo(errorInfo);
      setRetryCount(prev => prev + 1);
      
      // Error toast
      toast.error('Failed to generate prompt', {
        description: errorInfo.message,
        action: errorInfo.canRetry ? {
          label: 'Retry',
          onClick: () => void handleGeneratePrompt(),
        } : undefined,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetry = async () => {
    await handleGeneratePrompt();
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      setCopySuccess(true);
      
      // Success toast
      toast.success('Copied to clipboard!', {
        description: 'The prompt has been copied and is ready to paste.',
      });
      
      // Announce to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = 'Prompt copied to clipboard successfully.';
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);
      
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      toast.error('Failed to copy to clipboard', {
        description: 'Please manually select and copy the text.',
      });
    }
  };

  const handleShowSaveDialog = () => {
    setShowSaveDialog(true);
    setSaveError('');
    // Pre-populate title with a default based on reference and prompt type
    const promptTypeLabel = PROMPT_TYPES.find(t => t.value === selectedPromptType)?.label || 'Custom';
    setSaveTitle(`${promptTypeLabel} for "${reference.title}"`);
  };

  const handleSavePrompt = async () => {
    if (!saveTitle.trim()) {
      setSaveError('Please enter a title for the saved prompt');
      saveTitleRef.current?.focus();
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      await savePrompt({
        referenceId: reference._id,
        title: saveTitle.trim(),
        promptType: selectedPromptType,
        generatedPrompt,
        customInstructions: selectedPromptType === 'custom' ? customInstructions : undefined,
        description: saveDescription.trim() || undefined,
      });

      setSaveSuccess(true);
      setShowSaveDialog(false);
      setSaveTitle('');
      setSaveDescription('');
      
      // Success toast
      toast.success('Prompt saved successfully!', {
        description: 'Your prompt has been saved for future reference.',
      });
      
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save prompt');
      toast.error('Failed to save prompt', {
        description: 'Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Handle Escape key
    if (event.key === 'Escape') {
      event.preventDefault();
      if (showSaveDialog) {
        setShowSaveDialog(false);
      } else {
        onClose();
      }
      return;
    }

    // Handle Enter key for generation
    if (event.key === 'Enter' && event.ctrlKey && !isGenerating) {
      event.preventDefault();
      if (selectedPromptType === 'custom' && !customInstructions.trim()) {
        customInstructionsRef.current?.focus();
        return;
      }
      void handleGeneratePrompt();
    }

    // Handle Tab navigation within modal
    if (event.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
        
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div 
        ref={modalRef}
        className="modal-box max-w-4xl w-full max-h-[90vh] flex flex-col"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 id="modal-title" className="text-xl font-semibold">AI Prompt Generator</h2>
            <p id="modal-description" className="text-sm opacity-70 mt-1">
              Generate AI prompts for "{reference.title}"
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost"
            aria-label="Close modal"
            title="Close modal (Esc)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Prompt Type Selection */}
          <div className="mb-6">
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-3">
                Select Prompt Type
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" role="radiogroup">
                {PROMPT_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedPromptType(type.value)}
                    className={`btn btn-outline text-left h-auto p-4 ${
                      selectedPromptType === type.value
                        ? 'btn-primary'
                        : ''
                    }`}
                    role="radio"
                    aria-checked={selectedPromptType === type.value}
                    aria-describedby={`type-${type.value}-desc`}
                  >
                    <div className="font-medium text-sm">{type.label}</div>
                    <div id={`type-${type.value}-desc`} className="text-xs text-gray-600 mt-1">{type.description}</div>
                  </button>
                ))}
              </div>
            </fieldset>
          </div>

          {/* Custom Instructions (for custom prompt type) */}
          {selectedPromptType === 'custom' && (
            <div className="mb-6">
              <label htmlFor="customInstructions" className="block text-sm font-medium text-gray-700 mb-2">
                Custom Instructions *
              </label>
              <textarea
                ref={customInstructionsRef}
                id="customInstructions"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder="Describe what you want the AI to help you create..."
                className="textarea textarea-bordered w-full h-24 resize-none"
                aria-describedby="custom-instructions-help"
                required={selectedPromptType === 'custom'}
              />
              <p id="custom-instructions-help" className="text-xs text-gray-500 mt-1">
                Provide specific details about what you want to create. Press Ctrl+Enter to generate.
              </p>
            </div>
          )}

          {/* Reference Preview */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Reference Preview</h3>
            <div className="card bg-base-200" role="region" aria-label="Reference information">
              <div className="card-body p-4">
              <div className="flex items-start space-x-4">
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gray-200 rounded-md overflow-hidden">
                    {reference.mediaItems?.[0]?.url ? (
                      reference.mediaItems[0].mediaType === 'video' ? (
                        <video
                          src={reference.mediaItems[0].url}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                          aria-label={`Video thumbnail for ${reference.title}`}
                        />
                      ) : (
                        <img
                          src={reference.mediaItems[0].url}
                          alt={`Image from ${reference.title}`}
                          className="w-full h-full object-cover"
                        />
                      )
                    ) : reference.imageUrl ? (
                      <img
                        src={reference.imageUrl}
                        alt={`Image from ${reference.title}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400" aria-label="No image available">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{reference.title}</div>
                  {reference.description && (
                    <div className="text-sm text-gray-600 mt-1 line-clamp-2">{reference.description}</div>
                  )}
                  {reference.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2" role="list" aria-label="Reference tags">
                      {reference.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag._id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                          role="listitem"
                        >
                          {tag.name}
                        </span>
                      ))}
                      {reference.tags.length > 3 && (
                        <span className="text-xs text-gray-500" role="listitem">+{reference.tags.length - 3} more</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="mb-6">
            <button
              ref={generateButtonRef}
              onClick={() => void handleGeneratePrompt()}
              disabled={isGenerating || (selectedPromptType === 'custom' && !customInstructions.trim())}
              className="btn btn-primary w-full"
              aria-describedby="generate-help"
            >
              {isGenerating ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" aria-hidden="true"></div>
                  <span>Generating AI Prompt...</span>
                  <span className="sr-only">Please wait while the AI prompt is being generated</span>
                </div>
              ) : (
                'Generate AI Prompt'
              )}
            </button>
            <p id="generate-help" className="text-xs text-gray-500 mt-1 text-center">
              {selectedPromptType === 'custom' 
                ? 'Custom instructions are required. Press Ctrl+Enter to generate.'
                : 'Press Ctrl+Enter to generate quickly.'
              }
            </p>
          </div>

          {/* Enhanced Error Display */}
          {errorInfo && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="polite">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">
                    {errorInfo.type === 'rate_limit' ? 'Rate Limit Exceeded' :
                     errorInfo.type === 'network' ? 'Connection Error' :
                     errorInfo.type === 'api_key' ? 'Authentication Error' :
                     errorInfo.type === 'server' ? 'Server Error' :
                     'Error Generating Prompt'}
                  </h3>
                  <div className="mt-1 text-sm text-red-700">{errorInfo.message}</div>
                  {retryCount > 0 && (
                    <div className="mt-1 text-xs text-red-600">
                      Attempt {retryCount} failed. 
                      {errorInfo.type === 'rate_limit' && ' Please wait before retrying.'}
                    </div>
                  )}
                  {errorInfo.canRetry && (
                    <div className="mt-3">
                      <button
                        onClick={() => void handleRetry()}
                        disabled={isGenerating}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isGenerating ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b border-red-600 mr-1" aria-hidden="true"></div>
                            Retrying...
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0V9a8 8 0 1115.356 2M15 15v5h.582m0 0H15m.582 0A8.001 8.001 0 0019.418 15m0 0V15a8 8 0 11-15.356-2" />
                            </svg>
                            Try Again
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Generated Prompt Display */}
          {generatedPrompt && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Generated Prompt</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => void handleCopyToClipboard()}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors flex items-center focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    aria-describedby="copy-help"
                    title="Copy prompt to clipboard"
                  >
                    {copySuccess ? (
                      <>
                        <svg className="w-4 h-4 mr-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Copied!</span>
                        <span className="sr-only">Prompt copied to clipboard successfully</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleShowSaveDialog}
                    className="px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    title="Save prompt for future use"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Save
                  </button>
                </div>
              </div>
              <div 
                className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto"
                role="region"
                aria-labelledby="prompt-heading"
                aria-describedby="copy-help"
              >
                <div className="sr-only" id="prompt-heading">Generated AI Prompt Content</div>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">
                  {generatedPrompt}
                </pre>
              </div>
              <p id="copy-help" className="text-xs text-gray-500 mt-1">
                Use the Copy button above or select the text manually to copy the prompt.
              </p>
            </div>
          )}

          {/* Save Success Message */}
          {saveSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md" role="alert" aria-live="polite">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Prompt saved successfully!</h3>
                  <div className="mt-1 text-sm text-green-700">Your AI prompt has been saved for future reference.</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-action">
          <div className="flex justify-between items-center w-full">
            <div className="flex space-x-3">
              {generatedPrompt && (
                <>
                  <button
                    onClick={() => void handleCopyToClipboard()}
                    className="btn btn-outline btn-sm flex items-center"
                    title="Copy prompt to clipboard"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy Prompt
                  </button>
                  <button
                    onClick={handleShowSaveDialog}
                    className="btn btn-success btn-sm flex items-center"
                    title="Save prompt for future use"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Save Prompt
                  </button>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="btn btn-neutral btn-sm"
              title="Close modal"
            >
              Close
            </button>
          </div>
        </div>

        {/* Enhanced Save Dialog Modal */}
        {showSaveDialog && (
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-dialog-title"
            aria-describedby="save-dialog-description"
          >
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 id="save-dialog-title" className="text-lg font-medium text-gray-900 mb-2">Save AI Prompt</h3>
              <p id="save-dialog-description" className="text-sm text-gray-600 mb-4">
                Save this generated prompt for future reference and reuse.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="saveTitle" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    ref={saveTitleRef}
                    type="text"
                    id="saveTitle"
                    value={saveTitle}
                    onChange={(e) => setSaveTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter a title for this prompt"
                    aria-describedby="title-help"
                    maxLength={100}
                    required
                  />
                  <p id="title-help" className="text-xs text-gray-500 mt-1">
                    A descriptive title to help you find this prompt later.
                  </p>
                </div>

                <div>
                  <label htmlFor="saveDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    id="saveDescription"
                    value={saveDescription}
                    onChange={(e) => setSaveDescription(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Add notes about this prompt..."
                    aria-describedby="description-help"
                    maxLength={500}
                  />
                  <p id="description-help" className="text-xs text-gray-500 mt-1">
                    Optional notes about when and how to use this prompt.
                  </p>
                </div>

                {saveError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="polite">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-4 w-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <div className="ml-2 text-sm text-red-700">{saveError}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSaveDialog(false)}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  title="Cancel and close save dialog"
                >
                  Cancel
                </button>
                <button
                  onClick={() => void handleSavePrompt()}
                  disabled={isSaving || !saveTitle.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  title={!saveTitle.trim() ? "Please enter a title to save" : "Save the prompt"}
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" aria-hidden="true"></div>
                      <span>Saving...</span>
                      <span className="sr-only">Please wait while the prompt is being saved</span>
                    </>
                  ) : (
                    'Save Prompt'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
} 